import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import './DataListView.less';
import { DataDefinitionTable } from 'components/DataDefinitionsEditor/DataDefinitionTable';
import { DiagramDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { SequentialEditSession } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/SequentialEditSession';
import { DataDesignerContext } from './DataDesignerContext';

export interface IDataListViewProps {
  className?: string;
  diagrams: DiagramDisplayData[];
  /**
   * In list view we can select just one row and consequently one input.
   * So if there is single selected diagram it will be selected in "List View" as well.
   */
  selectedInputId?: string;
  showInputColumn: boolean;
  onNameChanged: (id: string, newName: string) => Promise<string>;
  onTypeChanged: (id: string, newType?: DecisionsType) => void;
  onIsListChanged: (id: string, newIsList: boolean) => void;
  onRowDeleted: (id: string) => void;
  onSelectionChanged: (id?: string) => void;
  onRowMoveUp: (id: string) => void;
  onRowMoveDown: (id: string) => void;
  onAddRow: () => Promise<void>;
  onConstantValueChanged: (id: string, value: string) => Promise<void>;
  onGetEditMappingContextId: (id: string) => Promise<string>;
  startEditSelectMapping: (id: string) => Promise<SequentialEditSession>;
}

/**
 * Represents Input/Output Data as a table.
 * Every input/output has a corresponding row.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataListView({
  className,
  diagrams,
  showInputColumn,
  selectedInputId,
  onRowDeleted,
  onNameChanged,
  onTypeChanged,
  onIsListChanged,
  onSelectionChanged,
  onRowMoveUp,
  onRowMoveDown,
  onAddRow,
  onConstantValueChanged,
  onGetEditMappingContextId,
  startEditSelectMapping,
}: IDataListViewProps) {
  const { mappingContext } = useContext(DataDesignerContext);

  const handleDelete = useCallback(
    (index: number) => {
      onRowDeleted(diagrams[index].Id);
    },
    [diagrams, onRowDeleted]
  );

  const handleNameChanged = useCallback(
    (index: number, newName: string) => {
      return onNameChanged(diagrams[index].Id, newName);
    },
    [diagrams, onNameChanged]
  );

  const handleConstantValueChanged = useCallback(
    (index: number, value: string) => {
      return onConstantValueChanged?.(diagrams[index].Id, value);
    },
    [diagrams, onConstantValueChanged]
  );

  const handleGetEditMappingContextId = useCallback(
    (index: number) => {
      return onGetEditMappingContextId?.(diagrams[index].Id);
    },
    [diagrams, onGetEditMappingContextId]
  );

  const handleIsListChanged = useCallback(
    (index: number, newIsList: boolean) => {
      onIsListChanged(diagrams[index].Id, newIsList);
    },
    [diagrams, onIsListChanged]
  );

  const handleTypeChanged = useCallback(
    (index: number, newType?: DecisionsType) => {
      onTypeChanged(diagrams[index].Id, newType);
    },
    [diagrams, onTypeChanged]
  );

  const handleRowSelected = useCallback(
    (index: number) => {
      onSelectionChanged(diagrams[index].Id);
    },
    [diagrams, onSelectionChanged]
  );

  const handleRowMoveUp = useCallback(
    (index: number) => {
      onRowMoveUp(diagrams[index].Id);
    },
    [diagrams, onRowMoveUp]
  );

  const handleRowMoveDown = useCallback(
    (index: number) => {
      onRowMoveDown(diagrams[index].Id);
    },
    [diagrams, onRowMoveDown]
  );

  const handleAddRow = useCallback(() => {
    return onAddRow();
  }, [onAddRow]);

  const handleStartEditSelectMapping = useCallback(
    (index: number) => {
      return startEditSelectMapping(diagrams[index].Id);
    },
    [diagrams, startEditSelectMapping]
  );

  const handleDeselect = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget === e.target) {
        onSelectionChanged(undefined);
      }
    },
    [onSelectionChanged]
  );

  const selectedRowIndex = diagrams.findIndex(item => item.Id === selectedInputId);
  const definitions = useMemo(
    () =>
      diagrams.map(input => ({
        Id: input.Id,
        DataName: input.DataDescription.Name,
        DataType: input.DataDescription.Type,
        IsArray: input.DataDescription.IsList,
        ValidationIssues: input.ValidationIssues,
        InlineMapping: input.InlineMapping,
      })),
    [diagrams]
  );

  return (
    <section className={classNames('dp-data-list-view', 'full-size', className)} onClick={handleDeselect}>
      <DataDefinitionTable
        showInputColumn={showInputColumn}
        selectedRowIndex={selectedRowIndex}
        definitions={definitions}
        onDelete={handleDelete}
        onNameChanged={handleNameChanged}
        onIsListChanged={handleIsListChanged}
        onTypeChanged={handleTypeChanged}
        onRowSelected={handleRowSelected}
        onMoveUp={handleRowMoveUp}
        onMoveDown={handleRowMoveDown}
        onAddRow={handleAddRow}
        onConstantValueChanged={handleConstantValueChanged}
        onGetEditMappingContextId={handleGetEditMappingContextId}
        mappingContext={mappingContext}
        startEditSelectMapping={handleStartEditSelectMapping}
      />
    </section>
  );
}
